import React, { useState, useEffect } from 'react'
import Modal from '../../../../ui_reusable/Modal'
import { Button, Message, Form } from 'rsuite'
import useStandardToaster from '../../../../ui_reusable/StandardToaster'
import { useQueryClient } from '@tanstack/react-query'
import type { MediaChannel } from '../../../../../api/generated/models'
import { useChannelsPartialUpdate } from '../../../../../api/generated/endpoints/media-channels/media-channels'

interface EditChannelProps {
  channel: MediaChannel
  open: boolean
  onClose: () => void
}

const EditChannel: React.FC<EditChannelProps> = ({
  channel,
  open,
  onClose,
}) => {
  const { pushToast } = useStandardToaster()
  const queryClient = useQueryClient()
  const [formValue, setFormValue] = useState({ name: channel.name })
  const [formError, setFormError] = useState<Record<string, string>>({})

  useEffect(() => {
    if (open) {
      setFormValue({ name: channel.name })
      setFormError({})
    }
  }, [open, channel.name])

  const updateMutation = useChannelsPartialUpdate({
    mutation: {
      onSuccess: () => {
        // Invalidate driver list queries to refresh the databook
        queryClient.invalidateQueries({
          queryKey: ['/api/v2/datasets/drivers/with-channels'],
        })
        queryClient.invalidateQueries({
          queryKey: ['/api/v2/datasets/drivers/without-channels'],
        })
        queryClient.invalidateQueries({
          queryKey: ['/api/v2/datasets/drivers/'],
        })
        queryClient.invalidateQueries({
          queryKey: ['/api/channels/'],
        })

        pushToast(
          <Message type="success">Channel updated successfully!</Message>,
          { placement: 'topCenter', duration: 5000 },
        )
        onClose()
      },
      onError: (error: any) => {
        const errorMessage =
          error?.response?.data?.detail ||
          error?.response?.data?.message ||
          error?.message ||
          'Failed to update channel'

        pushToast(<Message type="error">{errorMessage}</Message>, {
          placement: 'topCenter',
          duration: 5000,
        })
      },
    },
  })

  const handleSubmit = async () => {
    // Validate form
    const errors: Record<string, string> = {}

    if (!formValue.name || formValue.name.trim() === '') {
      errors.name = 'Channel name is required'
    }

    if (Object.keys(errors).length > 0) {
      setFormError(errors)
      return
    }

    try {
      await updateMutation.mutateAsync({
        id: channel.id!,
        data: {
          name: formValue.name.trim(),
        },
      })
    } catch (error: any) {
      // Error handling is done in onError callback
      console.error('Update channel error:', error)
    }
  }

  return (
    <Modal open={open} onClose={onClose} title={`Edit ${channel.name}`}>
      <Modal.Body>
        <Form
          fluid
          formValue={formValue}
          onChange={(value) => setFormValue(value as { name: string })}
          formError={formError}
        >
          <Form.Group controlId="name">
            <Form.ControlLabel>Channel Name</Form.ControlLabel>
            <Form.Control
              name="name"
              autoComplete="off"
              errorMessage={formError.name}
              errorPlacement="bottomStart"
            />
          </Form.Group>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button
          onClick={handleSubmit}
          appearance="primary"
          disabled={updateMutation.isPending}
        >
          {updateMutation.isPending ? 'Updating...' : 'Update Channel'}
        </Button>
      </Modal.Footer>
    </Modal>
  )
}

export default EditChannel
